import React, { useState } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { SEO } from '../components/shared/SEO';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { MobileFooterNav } from '../components/layout/MobileFooterNav';
import { PharmacistChat } from '../components/services/PharmacistChat';
import { AnnouncementBar } from '../components/layout/AnnouncementBar';
import { MobileDeliverToPill } from '../components/layout/MobileDeliverToPill';
import { PageHeroBreadcrumb } from '../components/layout/PageHeroBreadcrumb';
import { MobileHomeHeader } from '../components/MobileHomeHeader';
import { PackageIcon, TruckIcon, CheckCircleIcon, MapPinIcon, PhoneIcon, HomeIcon, ChevronRightIcon } from 'lucide-react';
export function TrackOrderPage() {
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<{
    name: string;
  } | null>(null);
  const [orderNumber, setOrderNumber] = useState('');
  const [trackingData, setTrackingData] = useState<any>(null);
  const handleTrack = (e: React.FormEvent) => {
    e.preventDefault();
    // Simulate tracking data
    setTrackingData({
      orderNumber: orderNumber,
      status: 'Out for Delivery',
      estimatedDelivery: 'Today, 2:00 PM - 5:00 PM',
      trackingNumber: 'TRK123456789',
      courier: 'Innova Express',
      courierPhone: '+966 920 000 000',
      timeline: [{
        status: 'Order Confirmed',
        description: 'Your order has been received and confirmed',
        timestamp: 'Jan 15, 2024 - 10:30 AM',
        completed: true
      }, {
        status: 'Processing',
        description: 'Your order is being prepared for shipment',
        timestamp: 'Jan 15, 2024 - 11:00 AM',
        completed: true
      }, {
        status: 'Shipped',
        description: 'Your order has been dispatched',
        timestamp: 'Jan 15, 2024 - 3:00 PM',
        completed: true
      }, {
        status: 'Out for Delivery',
        description: 'Your order is on the way to your location',
        timestamp: 'Jan 16, 2024 - 9:00 AM',
        completed: true,
        active: true
      }, {
        status: 'Delivered',
        description: 'Order delivered successfully',
        timestamp: 'Pending',
        completed: false
      }],
      items: [{
        name: 'Panadol Extra Pain Relief Tablets 24s',
        quantity: 2,
        image: 'https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?w=400&h=400&fit=crop'
      }, {
        name: 'Vitamin D3 Supplement',
        quantity: 1,
        image: 'https://images.unsplash.com/photo-1550572017-4bfa2d5c8f1d?w=400&h=400&fit=crop'
      }],
      deliveryAddress: 'King Fahd Road, Al Olaya, Riyadh 12211, Saudi Arabia'
    });
  };
  return <HelmetProvider data-id="element-8313" data-mp="3t0ykedigjyxo">
      <SEO title="Track Your Order" description="Track your Innova Pharmacy order in real-time. Enter your order number to see the current status and estimated delivery time." canonical="/track-order" data-id="element-8314" data-mp="3t1gp7yj6rlh7" />
      <div className="flex flex-col min-h-screen bg-gray-50" data-id="element-8315" data-mp="5uef6zhu04hq">
        {/* Desktop Header */}
        <div className="hidden md:block" data-id="element-8316" data-mp="aw3reigsio7e">
          <AnnouncementBar data-id="element-8317" data-mp="fu5b95m1irjx" />
          <MobileDeliverToPill selectedLocation={selectedLocation} onOpenLocationModal={() => setLocationModalOpen(true)} data-id="element-8318" data-mp="fsu1mp5wypkw" />
          <Header locationModalOpen={locationModalOpen} setLocationModalOpen={setLocationModalOpen} selectedLocation={selectedLocation} setSelectedLocation={setSelectedLocation} data-id="element-8319" data-mp="pjoptz2pzz8z" />
        </div>
        <main className="flex-grow pt-[var(--header-height-mobile)] md:pt-[var(--header-height-desktop)]" data-id="element-8320" data-mp="ylpql5x5x3fe">
          {/* Mobile Header Overlay - Only on mobile */}
          <div className="md:hidden fixed top-0 left-0 right-0 z-50" data-id="element-8321" data-mp="pw8vzc81elme">
            <MobileHomeHeader selectedLocation={selectedLocation} onOpenLocationModal={() => setLocationModalOpen(true)} alwaysWhiteBackground={true} data-id="element-8322" data-mp="puecm2cez1kf" />
          </div>
          {/* Page Hero Breadcrumb */}
          <PageHeroBreadcrumb breadcrumbs={[{
          label: 'Home',
          href: '/'
        }, {
          label: 'Track Order'
        }]} title="Track Your Order" description="Enter your order number to track your delivery" data-id="element-8323" data-mp="uose8mho64xj" />
          <div className="container mx-auto px-4 py-8" data-id="element-8324" data-mp="bx97c4kiy6q8">
            <div className="max-w-4xl mx-auto" data-id="element-8325" data-mp="bvod6g24gpsw">
              {!trackingData ? <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8" data-id="element-8326" data-mp="1s9wsbpkkbwui">
                  <div className="text-center mb-8" data-id="element-8327" data-mp="1nbd1bar43uva">
                    <div className="w-20 h-20 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-8328" data-mp="1uvno3f43ws58">
                      <PackageIcon className="w-10 h-10 text-[#1E2B55]" data-id="element-8329" data-mp="1zva2vszl2gg5" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-8330" data-mp="1lvhbdvg8fw74">
                      Track Your Order
                    </h2>
                    <p className="text-gray-600" data-id="element-8331" data-mp="1px2w22z0derv">
                      Enter your order number to see real-time tracking
                      information
                    </p>
                  </div>
                  <form onSubmit={handleTrack} className="max-w-md mx-auto space-y-6" data-id="element-8332" data-mp="2ikgn5kg0zg42">
                    <div data-id="element-8333" data-mp="28v6rxvpvxhbq">
                      <label htmlFor="orderNumber" className="block text-sm font-medium text-gray-700 mb-2" data-id="element-8334" data-mp="28udmqz6kqzvp">
                        Order Number
                      </label>
                      <input type="text" id="orderNumber" value={orderNumber} onChange={e => setOrderNumber(e.target.value)} placeholder="e.g., ORD-12345" required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E2B55] focus:border-transparent" data-id="element-8335" data-mp="31dklhj46uh50" />
                    </div>
                    <button type="submit" className="w-full bg-[#1E2B55] text-white font-bold py-3 rounded-lg hover:bg-[#162041] transition-colors" data-id="element-8336" data-mp="2nidubd1b16z0">
                      Track Order
                    </button>
                  </form>
                  <div className="mt-8 p-6 bg-blue-50 border border-blue-200 rounded-lg" data-id="element-8337" data-mp="29iw3ks4g7jn0">
                    <h3 className="font-semibold text-gray-900 mb-2" data-id="element-8338" data-mp="24jjiocx50mn2">
                      Where to find your order number?
                    </h3>
                    <ul className="text-sm text-gray-700 space-y-2" data-id="element-8339" data-mp="1uetahpb54mae">
                      <li className="flex items-start" data-id="element-8340" data-mp="1zdmpxn9zmnd7">
                        <span className="text-blue-600 mr-2" data-id="element-8341" data-mp="1zg8xhmwvp2vf">•</span>
                        Check your order confirmation email
                      </li>
                      <li className="flex items-start" data-id="element-8342" data-mp="29bkao0sn2212">
                        <span className="text-blue-600 mr-2" data-id="element-8343" data-mp="1bov7voq43le5">•</span>
                        Visit your account dashboard under "My Orders"
                      </li>
                      <li className="flex items-start" data-id="element-8344" data-mp="1go7p9ty7s09c">
                        <span className="text-blue-600 mr-2" data-id="element-8345" data-mp="1lmrdk10a5o4v">•</span>
                        Check the SMS confirmation sent to your phone
                      </li>
                    </ul>
                  </div>
                </div> : <div className="space-y-6" data-id="element-8346" data-mp="sw9ct6n13yum">
                  {/* Order Status Card */}
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6" data-id="element-8347" data-mp="xvm1lwxgnfzu">
                    <div className="flex items-start justify-between mb-6" data-id="element-8348" data-mp="2c46sdo24n7fr">
                      <div data-id="element-8349" data-mp="283d1clte1cin">
                        <h2 className="text-2xl font-bold text-gray-900 mb-1" data-id="element-8350" data-mp="2844r3g0b0g10">
                          Order {trackingData.orderNumber}
                        </h2>
                        <p className="text-gray-600" data-id="element-8351" data-mp="2h1ygs83kcbf7">
                          Tracking: {trackingData.trackingNumber}
                        </p>
                      </div>
                      <div className="text-right" data-id="element-8352" data-mp="2r1pe0hjid4iz">
                        <span className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-700 rounded-full text-sm font-medium" data-id="element-8353" data-mp="2r0xjzb5k7eut">
                          <TruckIcon className="w-4 h-4" data-id="element-8354" data-mp="2zszfcv0ru4y8" />
                          {trackingData.status}
                        </span>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-gray-50 rounded-lg" data-id="element-8355" data-mp="39rxcung6d8td">
                      <div data-id="element-8356" data-mp="3tdqjzbhpdbnz">
                        <p className="text-sm text-gray-600 mb-1" data-id="element-8357" data-mp="3teigqpn1dffu">
                          Estimated Delivery
                        </p>
                        <p className="font-semibold text-gray-900" data-id="element-8358" data-mp="17k703k0ot6m">
                          {trackingData.estimatedDelivery}
                        </p>
                      </div>
                      <div data-id="element-8359" data-mp="3oct4x7rzoxqp">
                        <p className="text-sm text-gray-600 mb-1" data-id="element-8360" data-mp="3og7281lsqgra">Courier</p>
                        <p className="font-semibold text-gray-900" data-id="element-8361" data-mp="39o6ot3ujg1yf">
                          {trackingData.courier}
                        </p>
                      </div>
                      <div data-id="element-8362" data-mp="30puryk32w7xj">
                        <p className="text-sm text-gray-600 mb-1" data-id="element-8363" data-mp="30qmhpe9zvbfw">Contact</p>
                        <a href={`tel:${trackingData.courierPhone}`} className="font-semibold text-[#1E2B55] hover:underline" data-id="element-8364" data-mp="35oo4oaajjuwn">
                          {trackingData.courierPhone}
                        </a>
                      </div>
                    </div>
                  </div>
                  {/* Tracking Timeline */}
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6" data-id="element-8365" data-mp="32wj8sb60damy">
                    <h3 className="text-xl font-bold text-gray-900 mb-6" data-id="element-8366" data-mp="2nyz4z7kullid">
                      Tracking Timeline
                    </h3>
                    <div className="space-y-6" data-id="element-8367" data-mp="2syli7h0of1r6">
                      {trackingData.timeline.map((event: any, index: number) => <div key={index} className="flex gap-4" data-id="element-8368" data-mp="3lk4r3rxt3p2z" data-mpi={index}>
                            <div className="flex flex-col items-center" data-id="element-8369" data-mp="36x31n2jjts3a">
                              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${event.completed ? event.active ? 'bg-blue-100 animate-pulse' : 'bg-green-100' : 'bg-gray-100'}`} data-id="element-8370" data-mp="3bvmsnhd01u2i">
                                <CheckCircleIcon className={`w-6 h-6 ${event.completed ? event.active ? 'text-blue-600' : 'text-green-600' : 'text-gray-400'}`} data-id="element-8371" data-mp="87uiy8x2hh43" />
                              </div>
                              {index < trackingData.timeline.length - 1 && <div className={`w-0.5 h-16 my-2 ${event.completed ? 'bg-green-200' : 'bg-gray-200'}`} data-id="element-8372" data-mp="1xnx1thpg7xkj"></div>}
                            </div>
                            <div className="flex-1 pb-4" data-id="element-8373" data-mp="150u770nw86zk">
                              <h4 className={`font-semibold ${event.completed ? 'text-gray-900' : 'text-gray-400'}`} data-id="element-8374" data-mp="1iz0yfxyc78q9">
                                {event.status}
                              </h4>
                              <p className={`text-sm ${event.completed ? 'text-gray-600' : 'text-gray-400'}`} data-id="element-8375" data-mp="2ufs9yqlfxy3t">
                                {event.description}
                              </p>
                              <p className="text-xs text-gray-500 mt-1" data-id="element-8376" data-mp="1ryyin1nqakqh">
                                {event.timestamp}
                              </p>
                            </div>
                          </div>)}
                    </div>
                  </div>
                  {/* Order Items */}
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6" data-id="element-8377" data-mp="199s65wdj2pd4">
                    <h3 className="text-xl font-bold text-gray-900 mb-4" data-id="element-8378" data-mp="1dbvliw3bie3o">
                      Order Items
                    </h3>
                    <div className="space-y-4" data-id="element-8379" data-mp="1i3dlbgtlu8n2">
                      {trackingData.items.map((item: any, index: number) => <div key={index} className="flex items-center gap-4" data-id="element-8380" data-mp="lk7d2knnubq4" data-mpi={index}>
                          <div className="w-16 h-16 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0" data-id="element-8381" data-mp="pkq5ogxdnltd">
                            <img src={item.image} alt={item.name} className="w-full h-full object-cover" data-id="element-8382" data-mp="i2059ewjn167" />
                          </div>
                          <div className="flex-1" data-id="element-8383" data-mp="43a6zvxzmvg9">
                            <h4 className="font-medium text-gray-900" data-id="element-8384" data-mp="42h9zmotzkbb">
                              {item.name}
                            </h4>
                            <p className="text-sm text-gray-600" data-id="element-8385" data-mp="3qhtkzrj1w8m7">
                              Quantity: {item.quantity}
                            </p>
                          </div>
                        </div>)}
                    </div>
                  </div>
                  {/* Delivery Address */}
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6" data-id="element-8386" data-mp="3ch91et5ba8rn">
                    <h3 className="text-xl font-bold text-gray-900 mb-4" data-id="element-8387" data-mp="37kimwa95nl8c">
                      Delivery Address
                    </h3>
                    <div className="flex items-start gap-3" data-id="element-8388" data-mp="1bifizyk8zjwx">
                      <MapPinIcon className="w-5 h-5 text-[#1E2B55] flex-shrink-0 mt-1" data-id="element-8389" data-mp="1gee5fgm0mrup" />
                      <p className="text-gray-700" data-id="element-8390" data-mp="11rb22tb0hozg">
                        {trackingData.deliveryAddress}
                      </p>
                    </div>
                  </div>
                  <button onClick={() => setTrackingData(null)} className="w-full border-2 border-[#1E2B55] text-[#1E2B55] font-bold py-3 rounded-lg hover:bg-[#F1F2F6] transition-colors" data-id="element-8391" data-mp="spbns8rdsz07">
                    Track Another Order
                  </button>
                </div>}
            </div>
          </div>
        </main>
        <Footer data-id="element-8392" data-mp="2d5y7qygnp5a3" />
        <MobileFooterNav data-id="element-8393" data-mp="2d5eye652np7o" />
        <PharmacistChat data-id="element-8394" data-mp="2d67v0eabgyc3" />
        <div className="h-16 md:hidden" data-id="element-8395" data-mp="2d3vhqefi7c5d"></div>
      </div>
    </HelmetProvider>;
}